import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  Box,
  CircularProgress,
  InputBase,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  useAccountMonthlyGrid,
  type AccountMonthlyGridRow,
  type DepletionsFrequency,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { fmt6PkNumber } from '../lib/volume';
import { colors } from '../theme';
import { PremisesBadge } from './PremisesBadge';
import { SectionExplainer } from './SectionExplainer';
import { PremiumPagination } from './depletions/PremiumPagination';
import { useSectionExport } from '../hooks/useSectionExport';
import {
  accountColumns,
  excelSheet,
  num as toNum,
  premisesLabel,
  type ExcelColumn,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

// ----------------------------------------------------------------
// Frequency presentation
// ----------------------------------------------------------------

const FREQ_STYLE: Record<
  DepletionsFrequency,
  { color: string; describe: string; rangeLabel: string }
> = {
  Monthly: {
    color: colors.freqColors.Monthly,
    describe: 'The bedrock — buys nearly every month',
    rangeLabel: '12+ active months',
  },
  'Bi-Monthly': {
    color: colors.freqColors['Bi-Monthly'],
    describe: 'Steady customers — every other month',
    rangeLabel: '6–11 active months',
  },
  Quarterly: {
    color: colors.freqColors.Quarterly,
    describe: 'Seasonal / project-driven buyers',
    rangeLabel: '3–5 active months',
  },
  Infrequent: {
    color: colors.freqColors.Infrequent,
    describe: 'One-and-done or just starting out',
    rangeLabel: '<3 active months',
  },
};

const FREQ_ORDER: DepletionsFrequency[] = ['Monthly', 'Bi-Monthly', 'Quarterly', 'Infrequent'];

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------

function shortMonthLabel(iso: string): string {
  const [year, month] = iso.split('-');
  const m = month ? Number(month) : 1;
  const y = year ? year.slice(-2) : '';
  return `${MONTH_NAMES[m - 1] ?? '?'} '${y}`;
}

// Renders a period label for the current granularity. Month uses the
// existing short label; quarter shows "Q2 '24"; year shows "2024".
// The aggregated bucket key uses month-1 (Q1 → `-01-01`) or year start
// (`-01-01`), but quarters carry an extra `-Q1..Q4` suffix.
function formatPeriodLabel(iso: string, granularity: Granularity): string {
  if (granularity === 'year') return iso.slice(0, 4);
  if (granularity === 'quarter') {
    const [year, qSeg] = iso.split('-Q');
    const yy = year ? year.slice(-2) : '';
    return `Q${qSeg ?? '?'} '${yy}`;
  }
  return shortMonthLabel(iso);
}

// Long form (used inside the cell tooltip's secondary title line).
function formatPeriodLong(iso: string, granularity: Granularity): string {
  if (granularity === 'year') return iso.slice(0, 4);
  if (granularity === 'quarter') {
    const [year, qSeg] = iso.split('-Q');
    return `Q${qSeg ?? '?'} ${year ?? ''}`;
  }
  return monthLong(iso);
}

// Strip the HY brand prefix so SKU names fit in the cell tooltip.
function shortSkuName(full: string): string {
  for (const prefix of ['Hooten Young ', 'Hooten & Young ', 'Hooten and Young ']) {
    if (full.startsWith(prefix)) return full.slice(prefix.length).trim();
  }
  return full;
}

function monthLong(iso: string): string {
  const [year, month] = iso.split('-');
  const m = month ? Number(month) : 1;
  return `${MONTH_NAMES_LONG[m - 1] ?? '?'} ${year ?? ''}`;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_NAMES_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// ----------------------------------------------------------------
// Premium light tooltip
// ----------------------------------------------------------------

interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 280 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

// ----------------------------------------------------------------
// Per-account row derivations — computed client-side from
// monthly_volumes so the backend stays untouched.
// ----------------------------------------------------------------

interface RowDerived {
  // Last 3 calendar months: how many were non-zero?
  recent3mActive: number;
  recent3mVol: number;
  // Length of the most recent stretch of consecutive non-zero months
  // ending at the latest period.
  currentStreak: number;
  // Longest historical streak anywhere in the series.
  longestStreak: number;
  // Months since the most recent non-zero cell (0 if currently active,
  // null if never active).
  monthsSinceActive: number | null;
  // First / last month (ISO date string) the account had non-zero
  // volume in the visible window. Both null if never active.
  firstActiveIso: string | null;
  lastActiveIso: string | null;
  // Average 9L per month over the entire series (including zero months).
  avgPerMonth: number;
  // Average 9L across only the active months. NaN-safe — 0 if never
  // active.
  avgPerActiveMonth: number;
  // Visual signal — derived label for the per-row sub-line.
  recencyLabel: string;
  recencyColor: string;
}

function deriveRow(monthly: { period: string; cases_9l: string }[]): RowDerived {
  const vals = monthly.map((m) => Number(m.cases_9l));
  const n = vals.length;
  const last3 = vals.slice(-3);
  const recent3mActive = last3.filter((v) => v > 0).length;
  const recent3mVol = last3.reduce((a, b) => a + b, 0);

  let currentStreak = 0;
  for (let i = n - 1; i >= 0; i--) {
    if (vals[i]! > 0) currentStreak++;
    else break;
  }
  let longestStreak = 0;
  let running = 0;
  for (const v of vals) {
    if (v > 0) {
      running++;
      if (running > longestStreak) longestStreak = running;
    } else {
      running = 0;
    }
  }
  let monthsSinceActive: number | null = null;
  let lastActiveIso: string | null = null;
  for (let i = n - 1; i >= 0; i--) {
    if (vals[i]! > 0) {
      monthsSinceActive = n - 1 - i;
      lastActiveIso = monthly[i]!.period;
      break;
    }
  }
  let firstActiveIso: string | null = null;
  for (let i = 0; i < n; i++) {
    if (vals[i]! > 0) {
      firstActiveIso = monthly[i]!.period;
      break;
    }
  }
  const total = vals.reduce((a, b) => a + b, 0);
  const avgPerMonth = n > 0 ? total / n : 0;
  const activeCount = vals.filter((v) => v > 0).length;
  const avgPerActiveMonth = activeCount > 0 ? total / activeCount : 0;

  let recencyLabel: string;
  let recencyColor: string;
  if (recent3mActive === 3) {
    recencyLabel = 'Active 3/3';
    recencyColor = colors.success;
  } else if (recent3mActive === 2) {
    recencyLabel = 'Active 2/3';
    recencyColor = colors.textSecondary;
  } else if (recent3mActive === 1) {
    recencyLabel = 'Slowing 1/3';
    recencyColor = colors.warning;
  } else {
    recencyLabel = 'Silent 0/3';
    recencyColor = colors.error;
  }

  return {
    recent3mActive,
    recent3mVol,
    currentStreak,
    longestStreak,
    monthsSinceActive,
    firstActiveIso,
    lastActiveIso,
    avgPerMonth,
    avgPerActiveMonth,
    recencyLabel,
    recencyColor,
  };
}

// ----------------------------------------------------------------
// Sort / filter
// ----------------------------------------------------------------

type SortKey = 'cadence' | 'volume' | 'recent' | 'streak' | 'name';
type FilterKey = 'all' | 'active' | 'slowing' | 'silent';

const SORT_OPTIONS: { key: SortKey; label: string; describe: string }[] = [
  { key: 'cadence', label: 'Cadence', describe: 'Active months desc, then volume' },
  { key: 'volume', label: 'Volume', describe: 'Total 6-pk desc' },
  { key: 'recent', label: 'Recent activity', describe: 'Last 3M volume desc' },
  { key: 'streak', label: 'Current streak', describe: 'Latest consecutive active months' },
  { key: 'name', label: 'Account A→Z', describe: 'Alphabetical' },
];

const FILTER_OPTIONS: { key: FilterKey; label: string; describe: string }[] = [
  { key: 'all', label: 'All accounts', describe: 'Show every row in the grid' },
  { key: 'active', label: 'Active (3/3)', describe: 'Bought every month for the last 3' },
  { key: 'slowing', label: 'Slowing', describe: '1–2 of last 3 months' },
  { key: 'silent', label: 'Silent (0/3)', describe: 'No depletions in last 3M' },
];

// ----------------------------------------------------------------
// Main component
// ----------------------------------------------------------------

// Visual window over the series. As more months accumulate over
// time, the heatmap would otherwise grow horizontally without bound;
// the default of 24 months keeps the section legible on a typical
// dashboard width, and the user can expand to see history.
type WindowKey = 12 | 24 | 36 | 'all';
const WINDOW_OPTIONS: { key: WindowKey; label: string; describe: string }[] = [
  { key: 12, label: '12M', describe: 'Last 12 months only' },
  { key: 24, label: '24M', describe: 'Last 24 months (default)' },
  { key: 36, label: '36M', describe: 'Last 36 months' },
  { key: 'all', label: 'All', describe: 'Every month in the dataset' },
];

// View granularity — month is the source of truth; quarter and year
// are aggregated client-side from the windowed monthly volumes.
type Granularity = 'month' | 'quarter' | 'year';
const GRANULARITY_OPTIONS: { key: Granularity; label: string; describe: string }[] = [
  { key: 'month', label: 'Month', describe: 'One cell per calendar month (default)' },
  {
    key: 'quarter',
    label: 'Quarter',
    describe: 'Sum each calendar quarter — Q1/Q2/Q3/Q4',
  },
  { key: 'year', label: 'Year', describe: 'Sum each calendar year — yearly comparison' },
];

export function AccountMonthlyHeatmap() {
  const [topN, setTopN] = useState<20 | 50 | 100>(50);
  const [windowKey, setWindowKey] = useState<WindowKey>(24);
  const [granularity, setGranularity] = useState<Granularity>('month');
  // Pagination — heatmap rows get heavy at 50+, so default 10/page
  // matches the other depletions tables.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [selectedFreqs, setSelectedFreqs] = useState<Set<DepletionsFrequency>>(new Set());
  const [sort, setSort] = useState<SortKey>('cadence');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);

  const { data, isLoading, error } = useAccountMonthlyGrid(topN);

  // Trim the months + each account's per-month volumes to the
  // selected window. This is the SINGLE place that applies the
  // window; everything below (rowsAll, cellScale, headline stats,
  // visible) derives from `windowed` so the section never grows
  // beyond what the user picked.
  const windowed = useMemo<{ months: string[]; accounts: AccountMonthlyGridRow[] } | null>(() => {
    if (!data) return null;
    const fullMonths = data.months.map((m) => String(m));
    if (windowKey === 'all' || fullMonths.length <= windowKey) {
      return { months: fullMonths, accounts: data.accounts };
    }
    const start = fullMonths.length - windowKey;
    return {
      months: fullMonths.slice(start),
      accounts: data.accounts.map((a) => ({
        ...a,
        monthly_volumes: a.monthly_volumes.slice(start),
        // Reflect months-active inside the visible window so the
        // cadence pill matches what's on screen (e.g. "11/12").
        months_active: a.monthly_volumes.slice(start).filter((c) => Number(c.cases_9l) > 0).length,
      })),
    };
  }, [data, windowKey]);

  // Granularity aggregation — folds the windowed monthly volumes into
  // quarter or year buckets. Months stay as-is. Product breakdowns
  // are merged by product_id so each bucket carries the same kind of
  // mix info the monthly view does.
  const aggregated = useMemo<{ months: string[]; accounts: AccountMonthlyGridRow[] } | null>(() => {
    if (!windowed) return null;
    if (granularity === 'month') return windowed;
    const bucketKey = (iso: string) =>
      granularity === 'year'
        ? `${iso.slice(0, 4)}-01-01`
        : `${iso.slice(0, 4)}-Q${Math.floor((Number(iso.split('-')[1]!) - 1) / 3) + 1}`;
    // Establish the unique ordered bucket axis from the months list.
    const orderedKeys: string[] = [];
    const seen = new Set<string>();
    for (const m of windowed.months) {
      const k = bucketKey(m);
      if (!seen.has(k)) {
        seen.add(k);
        orderedKeys.push(k);
      }
    }
    return {
      months: orderedKeys,
      accounts: windowed.accounts.map((a) => {
        const byBucket = new Map<
          string,
          {
            cases_9l: number;
            products: Map<number, { product_id: number; product_name: string; cases_9l: number }>;
          }
        >();
        for (const cell of a.monthly_volumes) {
          const k = bucketKey(cell.period);
          let slot = byBucket.get(k);
          if (!slot) {
            slot = { cases_9l: 0, products: new Map() };
            byBucket.set(k, slot);
          }
          slot.cases_9l += Number(cell.cases_9l);
          for (const p of cell.products) {
            const existing = slot.products.get(p.product_id);
            if (existing) {
              existing.cases_9l += Number(p.cases_9l);
            } else {
              slot.products.set(p.product_id, {
                product_id: p.product_id,
                product_name: p.product_name,
                cases_9l: Number(p.cases_9l),
              });
            }
          }
        }
        const monthly_volumes = orderedKeys.map((k) => {
          const slot = byBucket.get(k);
          if (!slot) return { period: k, cases_9l: '0', products: [] };
          const products = Array.from(slot.products.values())
            .sort((p1, p2) => p2.cases_9l - p1.cases_9l)
            .map((p) => ({
              product_id: p.product_id,
              product_name: p.product_name,
              cases_9l: String(p.cases_9l),
            }));
          return { period: k, cases_9l: String(slot.cases_9l), products };
        });
        // months_active inside the visible buckets — "active in 3 of
        // 8 quarters" rather than "of 24 months".
        const bucketsActive = monthly_volumes.filter((c) => Number(c.cases_9l) > 0).length;
        return {
          ...a,
          monthly_volumes,
          months_active: bucketsActive,
        };
      }),
    };
  }, [windowed, granularity]);

  // Per-account derived metrics — computed from the AGGREGATED series
  // so "Active 3/3" reflects the user's current view (last 3 months /
  // last 3 quarters / last 3 years).
  const rowsAll = useMemo(() => {
    if (!aggregated) return [];
    return aggregated.accounts.map((a) => ({
      ...a,
      derived: deriveRow(a.monthly_volumes),
    }));
  }, [aggregated]);

  // Headline metric — % of total volume coming from Monthly buyers.
  const portfolio = useMemo(() => {
    const stats: Record<DepletionsFrequency, { count: number; total: number }> = {
      Monthly: { count: 0, total: 0 },
      'Bi-Monthly': { count: 0, total: 0 },
      Quarterly: { count: 0, total: 0 },
      Infrequent: { count: 0, total: 0 },
    };
    let totalVol = 0;
    let totalAccounts = 0;
    for (const r of rowsAll) {
      const v = Number(r.total_9l);
      stats[r.frequency].count += 1;
      stats[r.frequency].total += v;
      totalVol += v;
      totalAccounts += 1;
    }
    return { stats, totalVol, totalAccounts };
  }, [rowsAll]);

  // Available states for the multi-select filter.
  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const r of rowsAll) if (r.state_code) set.add(r.state_code);
    return [...set].sort();
  }, [rowsAll]);

  // 95th percentile across non-zero cells — keeps a handful of huge
  // outliers from washing out the rest of the grid.
  const cellScale = useMemo(() => {
    if (rowsAll.length === 0) return { p95: 1 };
    const values: number[] = [];
    for (const a of rowsAll) {
      for (const m of a.monthly_volumes) {
        const v = Number(m.cases_9l);
        if (v > 0) values.push(v);
      }
    }
    if (values.length === 0) return { p95: 1 };
    values.sort((a, b) => a - b);
    const p95 = values[Math.floor(values.length * 0.95)] ?? 1;
    return { p95: Math.max(p95, 1) };
  }, [rowsAll]);

  // Filtered + sorted view.
  const visible = useMemo(() => {
    let rows = rowsAll.slice();

    // Recent-activity filter
    if (filter === 'active') {
      rows = rows.filter((r) => r.derived.recent3mActive === 3);
    } else if (filter === 'slowing') {
      rows = rows.filter((r) => r.derived.recent3mActive >= 1 && r.derived.recent3mActive <= 2);
    } else if (filter === 'silent') {
      rows = rows.filter((r) => r.derived.recent3mActive === 0);
    }

    // State multi-select
    if (selectedStates.size > 0) {
      rows = rows.filter((r) => r.state_code !== null && selectedStates.has(r.state_code));
    }

    // Frequency tier filter
    if (selectedFreqs.size > 0) {
      rows = rows.filter((r) => selectedFreqs.has(r.frequency));
    }

    // Search
    const q = searchQuery.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.name.toLowerCase().includes(q));

    // Sort
    switch (sort) {
      case 'volume':
        rows.sort((a, b) => Number(b.total_9l) - Number(a.total_9l));
        break;
      case 'recent':
        rows.sort((a, b) => b.derived.recent3mVol - a.derived.recent3mVol);
        break;
      case 'streak':
        rows.sort(
          (a, b) =>
            b.derived.currentStreak - a.derived.currentStreak ||
            Number(b.total_9l) - Number(a.total_9l),
        );
        break;
      case 'name':
        rows.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'cadence':
      default:
        rows.sort(
          (a, b) => b.months_active - a.months_active || Number(b.total_9l) - Number(a.total_9l),
        );
    }
    return rows;
  }, [rowsAll, filter, selectedStates, selectedFreqs, searchQuery, sort]);

  // Reset to page 1 whenever the visible set changes so the user
  // doesn't end up on an empty page after narrowing the list.
  useEffect(() => {
    setPage(0);
  }, [sort, filter, selectedStates, selectedFreqs, searchQuery, windowKey, topN, rowsPerPage]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return visible;
    const start = page * rowsPerPage;
    return visible.slice(start, start + rowsPerPage);
  }, [visible, page, rowsPerPage]);

  // Recent cells — in monthly view it's the last 3 months ("current
  // quarter"). In quarter / year views the same "last 3 cells" idea
  // applies (last 3 quarters / last 3 years), with the same indigo
  // tint used as the recent-attention zone.
  const recentQuarterMonths = useMemo(() => {
    if (!aggregated) return new Set<string>();
    return new Set(aggregated.months.slice(-3));
  }, [aggregated]);

  // Vertical year dividers — column index of every January (monthly
  // view) or every Q1 (quarterly view). In yearly view every cell IS
  // a year so no dividers are needed.
  const yearBoundaries = useMemo(() => {
    if (!aggregated || granularity === 'year') return new Set<number>();
    const set = new Set<number>();
    let prevYear: string | null = null;
    aggregated.months.forEach((iso, i) => {
      const yr = iso.slice(0, 4);
      if (prevYear !== null && yr !== prevYear) set.add(i);
      prevYear = yr;
    });
    return set;
  }, [aggregated, granularity]);

  // Excel export: all loaded (top-N) rows of `visible`, ignoring pagination,
  // with one column per displayed period.
  useSectionExport((): ExcelWorkbookSpec | null => {
    if (!aggregated) return null;
    type Row = (typeof visible)[number];
    const periodColumns: ExcelColumn<Row>[] = aggregated.months.map((iso, i) => ({
      header: formatPeriodLabel(iso, granularity),
      value: (r) => {
        const v = toNum(r.monthly_volumes[i]?.cases_9l);
        return v ? v : null;
      },
      format: 'decimal',
      width: 9,
    }));
    const unit =
      granularity === 'month' ? 'Months' : granularity === 'quarter' ? 'Quarters' : 'Years';
    const firstMonth = windowed?.months[0];
    const lastMonth = windowed?.months[windowed.months.length - 1];
    const sortLabel = SORT_OPTIONS.find((o) => o.key === sort)?.label ?? 'Cadence';
    const filters: ExportFilter[] = [
      { label: 'Accounts shown', value: `Top ${topN} by volume` },
      {
        label: 'View by',
        value: GRANULARITY_OPTIONS.find((o) => o.key === granularity)?.label ?? 'Month',
      },
      {
        label: 'Time window',
        value: windowKey === 'all' ? 'All months' : `Last ${windowKey} months`,
      },
      {
        label: 'Search (account name)',
        value: searchQuery.trim() ? `"${searchQuery.trim()}"` : '',
      },
      {
        label: 'State',
        value: selectedStates.size > 0 ? [...selectedStates].sort().join(', ') : 'All states',
      },
      {
        label: 'Frequency',
        value:
          selectedFreqs.size > 0
            ? FREQ_ORDER.filter((f) => selectedFreqs.has(f)).join(', ')
            : 'All',
      },
      {
        label: 'Activity status',
        value: FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All accounts',
      },
      {
        kind: 'sort',
        label: 'Sorted by',
        value: sort === 'name' ? 'Account name (A → Z)' : `${sortLabel} (high → low)`,
      },
      { kind: 'info', label: 'Data through', value: data?.period_end ?? '' },
      {
        kind: 'info',
        label: 'Months covered',
        value:
          firstMonth && lastMonth ? `${firstMonth.slice(0, 7)} to ${lastMonth.slice(0, 7)}` : '',
      },
    ];
    return {
      title: 'Account Activity Heatmap',
      filters,
      sheets: [
        excelSheet<Row>({
          name: 'Activity',
          rows: visible,
          columns: [
            ...accountColumns<Row>(),
            { header: 'Distributor', value: (r) => r.distributor_code, width: 14 },
            {
              header: 'Premises',
              value: (r) => premisesLabel(r.premises_type, r.is_online_retailer),
              width: 12,
            },
            { header: 'Total 9L', value: (r) => toNum(r.total_9l), format: 'decimal' },
            { header: `${unit} active`, value: (r) => r.months_active, format: 'int' },
            { header: 'Frequency', value: (r) => r.frequency, width: 12 },
            ...periodColumns,
          ],
        }),
      ],
    };
  }, !!data && !isLoading);

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load purchase velocity: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || !data) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Purchase Velocity Heatmap
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  const monthlyPct =
    portfolio.totalVol > 0
      ? Math.round((portfolio.stats.Monthly.total / portfolio.totalVol) * 100)
      : 0;

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Cadence';
  const filterLabel = FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All accounts';

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <SpeedIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Purchase Velocity Heatmap
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top {topN} accounts by total 6-pk ·{' '}
            {granularity === 'month'
              ? 'month-by-month'
              : granularity === 'quarter'
                ? 'quarter-by-quarter'
                : 'year-by-year'}{' '}
            ordering intensity · {aggregated!.months.length}{' '}
            {granularity === 'month' ? 'months' : granularity === 'quarter' ? 'quarters' : 'years'}{' '}
            ({formatPeriodLabel(aggregated!.months[0]!, granularity)} →{' '}
            {formatPeriodLabel(aggregated!.months[aggregated!.months.length - 1]!, granularity)})
          </Typography>
        </Box>
      </Box>

      {/* Headline insight */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          px: 2.5,
          py: 1.75,
          mb: 2.5,
          borderRadius: 1.5,
          background: `linear-gradient(135deg, ${colors.chartPrimary}10 0%, ${colors.freqColors.Monthly}08 100%)`,
          border: `1px solid ${colors.chartPrimary}26`,
        }}
      >
        <Typography
          sx={{
            fontSize: 36,
            fontWeight: 800,
            color: colors.chartPrimary,
            lineHeight: 1,
            fontFeatureSettings: '"tnum","lnum"',
            letterSpacing: '-0.02em',
          }}
        >
          {monthlyPct}%
        </Typography>
        <Box>
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.3 }}
          >
            of total volume comes from Monthly buyers
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, mt: 0.25 }}>
            <strong style={{ color: colors.freqColors.Monthly, fontWeight: 700 }}>
              {portfolio.stats.Monthly.count}
            </strong>{' '}
            accounts order nearly every month, driving{' '}
            <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>
              {fmt6PkNumber(portfolio.stats.Monthly.total)}
            </strong>{' '}
            6-pk of the {fmt6PkNumber(portfolio.totalVol)} 6-pk total across the top {topN}.
          </Typography>
        </Box>
      </Box>

      {/* Frequency cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {FREQ_ORDER.map((freq) => {
          const style = FREQ_STYLE[freq];
          const stats = portfolio.stats[freq];
          const pctAccts =
            portfolio.totalAccounts > 0
              ? Math.round((stats.count / portfolio.totalAccounts) * 100)
              : 0;
          const pctVol =
            portfolio.totalVol > 0 ? Math.round((stats.total / portfolio.totalVol) * 100) : 0;
          const isSelected = selectedFreqs.has(freq);
          return (
            <Tooltip
              key={freq}
              title={
                <Box sx={{ p: 0.25 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                    {freq} buyers
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: colors.textSecondary, mb: 0.4 }}>
                    {style.describe}.
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                    Click to {isSelected ? 'remove' : 'filter to'} this tier only.
                  </Typography>
                </Box>
              }
            >
              <Box
                component="button"
                onClick={() => {
                  const next = new Set(selectedFreqs);
                  if (next.has(freq)) next.delete(freq);
                  else next.add(freq);
                  setSelectedFreqs(next);
                }}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  border: `1px solid ${isSelected ? style.color : style.color + '33'}`,
                  bgcolor: isSelected ? `${style.color}10` : 'transparent',
                  overflow: 'hidden',
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: style.color,
                    bgcolor: `${style.color}08`,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${style.color}55`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ height: 3, bgcolor: style.color, opacity: isSelected ? 1 : 0.6 }} />
                <Box sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: style.color,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {freq}
                    </Typography>
                    {isSelected && (
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: style.color,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Filtered
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 800,
                        color: colors.textPrimary,
                        lineHeight: 1,
                        fontFeatureSettings: '"tnum","lnum"',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {stats.count}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: colors.textMuted }}>
                      accts · {pctAccts}%
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      mt: 0.4,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                      {fmt6PkNumber(stats.total)}
                    </Box>{' '}
                    6-pk · {pctVol}% of volume
                  </Typography>
                  <Typography
                    sx={{ fontSize: 10, color: colors.textMuted, mt: 0.3, fontStyle: 'italic' }}
                  >
                    {style.rangeLabel}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={`Search top ${topN} by name…`}
        />
        <StateMultiSelect
          available={availableStates}
          selected={selectedStates}
          onChange={setSelectedStates}
          open={stateMenuOpen}
          onToggle={() => {
            setStateMenuOpen((o) => !o);
            setSortMenuOpen(false);
            setFilterMenuOpen(false);
          }}
        />
        <ToolbarMenu
          label="Sort"
          value={sortLabel}
          open={sortMenuOpen}
          onToggle={() => {
            setSortMenuOpen((o) => !o);
            setFilterMenuOpen(false);
            setStateMenuOpen(false);
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={sort === opt.key}
              primary={opt.label}
              secondary={opt.describe}
              onClick={() => {
                setSort(opt.key);
                setSortMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <ToolbarMenu
          label="Filter"
          value={filterLabel}
          open={filterMenuOpen}
          onToggle={() => {
            setFilterMenuOpen((o) => !o);
            setSortMenuOpen(false);
            setStateMenuOpen(false);
          }}
        >
          {FILTER_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={filter === opt.key}
              primary={opt.label}
              secondary={opt.describe}
              onClick={() => {
                setFilter(opt.key);
                setFilterMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visible.length} of {rowsAll.length} accounts
        </Typography>
      </Box>

      {/* Data-shape row — fetch size + visible window. Kept distinct
          from the exploration toolbar so day-to-day controls (search,
          state, sort, filter) aren't fighting these "set once" knobs
          for attention. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          flexWrap: 'wrap',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.25,
          bgcolor: colors.surfaceSubtle,
          border: `1px solid ${colors.borderLight}`,
        }}
      >
        <PillGroup label="Show">
          {([20, 50, 100] as const).map((n) => (
            <PillButton
              key={n}
              active={topN === n}
              onClick={() => setTopN(n)}
              tooltip={`Fetch the top ${n} accounts by 6-pk from the server`}
            >
              Top {n}
            </PillButton>
          ))}
        </PillGroup>
        <PillGroup label="View">
          {GRANULARITY_OPTIONS.map((opt) => (
            <PillButton
              key={opt.key}
              active={granularity === opt.key}
              onClick={() => setGranularity(opt.key)}
              tooltip={opt.describe}
            >
              {opt.label}
            </PillButton>
          ))}
        </PillGroup>
        <PillGroup label="Window">
          {WINDOW_OPTIONS.map((opt) => (
            <PillButton
              key={opt.key}
              active={windowKey === opt.key}
              onClick={() => setWindowKey(opt.key)}
              tooltip={opt.describe}
            >
              {opt.label}
            </PillButton>
          ))}
        </PillGroup>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {aggregated!.months.length}{' '}
          {granularity === 'month' ? 'months' : granularity === 'quarter' ? 'quarters' : 'years'}{' '}
          shown
        </Typography>
      </Box>

      {/* Grid */}
      <HeatmapGrid
        months={aggregated!.months}
        rows={paginated}
        cellScale={cellScale}
        recentQuarter={recentQuarterMonths}
        yearBoundaries={yearBoundaries}
        granularity={granularity}
      />

      <PremiumPagination
        count={visible.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        itemLabel="accounts"
      />

      {/* Legend */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>Volume intensity</Typography>
          <Box sx={{ display: 'flex', gap: '2px' }}>
            {[0.08, 0.2, 0.4, 0.6, 0.8, 0.95].map((stop) => (
              <Box
                key={stop}
                sx={{
                  width: 16,
                  height: 12,
                  bgcolor: colors.chartPrimary,
                  opacity: stop,
                  borderRadius: 0.5,
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontSize: 11, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
            low → high (capped at {fmt6PkNumber(cellScale.p95)} 6-pk)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: '#fff',
              border: `1px solid ${colors.borderLight}`,
            }}
          />
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>No depletion</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: 0.5,
              bgcolor: `${colors.chartPrimary}0d`,
              border: `1px dashed ${colors.chartPrimary}66`,
            }}
          />
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
            Current quarter (last 3 months)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 1.5,
              height: 14,
              bgcolor: colors.textMuted,
              opacity: 0.5,
            }}
          />
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>Year boundary</Typography>
        </Box>
      </Box>

      {/* Reader's guide. */}
      <SectionExplainer
        rows={[
          {
            sample: <HMSampleCellRow />,
            term: 'Cell intensity (color depth)',
            desc: (
              <>
                Each cell is one (account, month). Darker = higher 6-pk volume. White cells are
                zero-activity months. The scale is capped at the 95th-percentile cell value so a
                single outlier doesn't wash everything else out.
              </>
            ),
          },
          {
            sample: <HMSampleRecentTint />,
            term: 'Recent-quarter tint',
            desc: (
              <>
                The most-recent 3 months get a faint <strong>indigo wash</strong> across their
                entire column — helps your eye anchor "what's been happening lately" without
                changing the per-cell intensity.
              </>
            ),
          },
          {
            sample: <HMSampleFrequencyChip />,
            term: 'Frequency badge per row (Monthly / Bi-monthly / Quarterly / Infrequent)',
            desc: (
              <>
                Classifies the account's <strong>cadence</strong> based on how many months it had
                non-zero activity. Reps prioritize
                <strong> Monthly</strong> buyers for retention and <strong>Infrequent</strong>{' '}
                buyers for reactivation.
              </>
            ),
          },
          {
            sample: <HMSampleGranularityChip />,
            term: 'Granularity toggle (Month / Quarter / Year)',
            desc: (
              <>
                Roll cells up to quarterly or yearly totals when the monthly view is too dense.
                Aggregation happens client-side — the underlying data is the same.
              </>
            ),
          },
          {
            sample: <HMSampleRecencyChip />,
            term: 'Recency chip in the account label column',
            desc: (
              <>
                Tells you how long since the account's last non-zero month.{' '}
                <strong>0–60 days</strong> = active, <strong>61–120</strong> = drifting,{' '}
                <strong>120+</strong> = at-risk. Color-coded so dormant accounts stand out in the
                row labels.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

// ----------------------------------------------------------------
// Heatmap sample swatches
// ----------------------------------------------------------------

function HMSampleCellRow() {
  return (
    <Box sx={{ display: 'flex', gap: '1px' }}>
      {[0.1, 0.3, 0.55, 0.75, 1].map((a, i) => (
        <Box
          key={i}
          sx={{
            width: 9,
            height: 14,
            bgcolor: colors.chartPrimary,
            opacity: a,
            borderRadius: 0.3,
          }}
        />
      ))}
    </Box>
  );
}

function HMSampleRecentTint() {
  return (
    <Box sx={{ display: 'flex', gap: '1px', position: 'relative' }}>
      {[0.2, 0.5, 0.8, 0.6, 0.45].map((a, i) => (
        <Box
          key={i}
          sx={{
            width: 9,
            height: 14,
            bgcolor: colors.chartPrimary,
            opacity: a,
            borderRadius: 0.3,
            ...(i >= 2 ? { boxShadow: `inset 0 0 0 9px ${colors.chartPrimary}11` } : {}),
          }}
        />
      ))}
    </Box>
  );
}

function HMSampleFrequencyChip() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.05em',
        color: colors.success,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      Monthly
    </Box>
  );
}

function HMSampleGranularityChip() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        gap: 0,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1,
        p: 0.2,
        fontSize: 9,
        fontWeight: 700,
      }}
    >
      <Box
        component="span"
        sx={{ px: 0.5, py: 0.1, bgcolor: '#fff', borderRadius: 0.75, color: colors.chartPrimary }}
      >
        M
      </Box>
      <Box component="span" sx={{ px: 0.5, py: 0.1, color: colors.textMuted }}>
        Q
      </Box>
      <Box component="span" sx={{ px: 0.5, py: 0.1, color: colors.textMuted }}>
        Y
      </Box>
    </Box>
  );
}

function HMSampleRecencyChip() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.05em',
        color: colors.warning,
        bgcolor: `${colors.warning}14`,
        border: `1px solid ${colors.warning}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      45d
    </Box>
  );
}

// ----------------------------------------------------------------
// Heatmap grid — extracted so the rendering logic is self-contained
// ----------------------------------------------------------------

// ----------------------------------------------------------------
// Account label tooltip — rich hover content for the leftmost cell.
// Designed to give the rep everything they'd otherwise have to dig
// out of the Follow-Up tracker: full mailing address, distributor,
// customer-since, last activity, longest streak, avg per active month.
// ----------------------------------------------------------------

interface AccountLabelTooltipProps {
  acc: {
    name: string;
    address: string | null;
    state_code: string | null;
    city: string | null;
    county: string | null;
    zip_code: string | null;
    distributor_code: string | null;
    premises_type: string | null;
    total_9l: string;
    months_active: number;
    frequency: DepletionsFrequency;
    derived: RowDerived;
  };
  /** Number of months in the visible window — denominator for the
   *  "X / Y months" display. */
  monthsCount: number;
}

function AccountLabelTooltip({ acc, monthsCount }: AccountLabelTooltipProps) {
  const total = Number(acc.total_9l);
  const fStyle = FREQ_STYLE[acc.frequency];
  const cityStateZip = [[acc.city, acc.state_code].filter(Boolean).join(', '), acc.zip_code]
    .filter(Boolean)
    .join(' ');
  const hasAddress = Boolean(acc.address || acc.zip_code || acc.county);

  return (
    <Box sx={{ p: 0.25 }}>
      {/* Header — account name */}
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.4 }}>{acc.name}</Typography>

      {/* Address block */}
      <Box sx={{ mb: 0.6 }}>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            mb: 0.25,
          }}
        >
          Address
        </Typography>
        {hasAddress ? (
          <>
            {acc.address && (
              <Typography sx={{ fontSize: 11.5, color: colors.textPrimary, fontWeight: 600 }}>
                {acc.address}
              </Typography>
            )}
            {cityStateZip && (
              <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
                {cityStateZip}
              </Typography>
            )}
            {acc.county && (
              <Typography sx={{ fontSize: 10.5, color: colors.textMuted }}>
                {acc.county} County
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
            No address on file
          </Typography>
        )}
      </Box>

      {/* Distributor + tier */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          mb: 0.75,
          pb: 0.75,
          borderBottom: `1px solid ${colors.borderLight}`,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            Distributor
          </Typography>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: colors.textPrimary,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {acc.distributor_code ?? '—'}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            Cadence tier
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: fStyle.color }}>
            {acc.frequency}
          </Typography>
        </Box>
      </Box>

      {/* Activity facts */}
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 0.5,
        }}
      >
        Activity in window
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gap: '4px 12px',
          fontSize: 11,
          fontFeatureSettings: '"tnum"',
          alignItems: 'baseline',
        }}
      >
        <span style={{ opacity: 0.7 }}>Total volume</span>
        <span style={{ fontWeight: 700 }}>
          {fmt6PkNumber(total)} 6-pk · {num.format(total)} 9L
        </span>

        <span style={{ opacity: 0.7 }}>Active months</span>
        <span style={{ fontWeight: 700 }}>
          {acc.months_active} / {monthsCount}
        </span>

        <span style={{ opacity: 0.7 }}>Avg per active month</span>
        <span style={{ fontWeight: 700 }}>{fmt6PkNumber(acc.derived.avgPerActiveMonth)} 6-pk</span>

        <span style={{ opacity: 0.7 }}>Last 3M activity</span>
        <span style={{ fontWeight: 700, color: acc.derived.recencyColor }}>
          {acc.derived.recencyLabel}
        </span>

        <span style={{ opacity: 0.7 }}>Current streak</span>
        <span style={{ fontWeight: 700 }}>
          {acc.derived.currentStreak} {acc.derived.currentStreak === 1 ? 'mo' : 'mos'}
        </span>

        <span style={{ opacity: 0.7 }}>Longest streak</span>
        <span style={{ fontWeight: 700 }}>
          {acc.derived.longestStreak} {acc.derived.longestStreak === 1 ? 'mo' : 'mos'}
        </span>

        <span style={{ opacity: 0.7 }}>Customer since</span>
        <span style={{ fontWeight: 700 }}>
          {acc.derived.firstActiveIso ? shortMonthLabel(acc.derived.firstActiveIso) : '—'}
        </span>

        <span style={{ opacity: 0.7 }}>Last active</span>
        <span style={{ fontWeight: 700 }}>
          {acc.derived.lastActiveIso ? shortMonthLabel(acc.derived.lastActiveIso) : '—'}
        </span>
      </Box>
    </Box>
  );
}

interface HeatmapGridProps {
  months: string[];
  rows: Array<{
    account_id: number;
    name: string;
    address: string | null;
    state_code: string | null;
    city: string | null;
    county: string | null;
    zip_code: string | null;
    distributor_code: string | null;
    premises_type: string | null;
    is_online_retailer: boolean;
    total_9l: string;
    months_active: number;
    frequency: DepletionsFrequency;
    monthly_volumes: {
      period: string;
      cases_9l: string;
      products: { product_id: number; product_name: string; cases_9l: string }[];
    }[];
    derived: RowDerived;
  }>;
  cellScale: { p95: number };
  recentQuarter: Set<string>;
  yearBoundaries: Set<number>;
  granularity: Granularity;
}

function HeatmapGrid({
  months,
  rows,
  cellScale,
  recentQuarter,
  yearBoundaries,
  granularity,
}: HeatmapGridProps) {
  if (rows.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: colors.textMuted,
          fontSize: 12,
          border: `1px dashed ${colors.borderLight}`,
          borderRadius: 1.5,
        }}
      >
        No accounts match the current filters.
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Box
        sx={{
          display: 'grid',
          // account label · period cells (flexible) · frequency pill.
          // Cells grow with `1fr` so the grid always fills the card;
          // the min floor depends on granularity (month=26, quarter=
          // 60, year=120) so labels stay readable in each view.
          gridTemplateColumns: `260px repeat(${months.length}, minmax(${granularity === 'year' ? 120 : granularity === 'quarter' ? 60 : 26}px, 1fr)) 100px`,
          alignItems: 'center',
          gap: 0,
          width: '100%',
        }}
      >
        {/* Header row — account label is sticky so it stays visible
            when the grid scrolls horizontally on long histories. */}
        <Box
          sx={{
            pr: 1.5,
            position: 'sticky',
            left: 0,
            zIndex: 2,
            bgcolor: '#fff',
            borderRight: `1px solid ${colors.borderLight}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: colors.textMuted,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Account
          </Typography>
        </Box>
        {months.map((iso, idx) => {
          const isRecent = recentQuarter.has(iso);
          const startsYear = yearBoundaries.has(idx);
          return (
            <Box
              key={iso}
              sx={{
                textAlign: 'center',
                borderBottom: `1px solid ${colors.borderLight}`,
                borderLeft: startsYear ? `1.5px solid ${colors.textMuted}55` : 'none',
                bgcolor: isRecent ? `${colors.chartPrimary}08` : 'transparent',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: granularity === 'month' ? 9 : 10.5,
                  fontWeight: isRecent ? 700 : 600,
                  color: isRecent ? colors.chartPrimary : colors.textMuted,
                  // Month labels are vertical (lots of narrow cells);
                  // quarter / year labels are horizontal (fewer cells,
                  // plenty of room).
                  writingMode: granularity === 'month' ? 'vertical-rl' : 'horizontal-tb',
                  transform: granularity === 'month' ? 'rotate(180deg)' : 'none',
                  whiteSpace: 'nowrap',
                  py: 0.5,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {formatPeriodLabel(iso, granularity)}
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ pl: 1.5, textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: colors.textMuted,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Cadence
          </Typography>
        </Box>

        {/* Data rows */}
        {rows.map((acc) => {
          const freqStyle = FREQ_STYLE[acc.frequency];
          return (
            <Box key={acc.account_id} sx={{ display: 'contents' }}>
              {/* Label cell — sticky so the account stays anchored
                  while the user scrolls right through the months. */}
              <Box
                sx={{
                  pr: 1.5,
                  pl: 0.25,
                  py: 0.5,
                  borderBottom: `1px solid ${colors.borderLight}`,
                  borderRight: `1px solid ${colors.borderLight}`,
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: '#fff',
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Tooltip
                    maxWidth={300}
                    placement="right-start"
                    title={<AccountLabelTooltip acc={acc} monthsCount={months.length} />}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: colors.textPrimary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'help',
                        borderBottom: `1px dotted ${colors.borderLight}`,
                        display: 'inline-block',
                        maxWidth: '100%',
                      }}
                    >
                      {acc.name}
                    </Typography>
                  </Tooltip>
                  <PremisesBadge premises={acc.premises_type} online={acc.is_online_retailer} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    color: colors.textMuted,
                    whiteSpace: 'nowrap',
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {[acc.state_code, acc.city].filter(Boolean).join(' · ')} ·{' '}
                  {fmt6PkNumber(acc.total_9l)} 6-pk · {num.format(Number(acc.total_9l))} 9L
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.6,
                    mt: 0.2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: acc.derived.recencyColor,
                      letterSpacing: 0.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {acc.derived.recencyLabel}
                  </Typography>
                  {acc.derived.currentStreak >= 3 && (
                    <Typography
                      sx={{ fontSize: 9.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}
                    >
                      · 🔥{acc.derived.currentStreak}mo streak
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Cells */}
              {acc.monthly_volumes.map((cell, idx) => {
                const v = Number(cell.cases_9l);
                const ratio = v > 0 ? Math.min(1, v / cellScale.p95) : 0;
                // Perceptual ease — sqrt brings small values up a bit.
                const intensity = ratio > 0 ? 0.18 + 0.78 * Math.sqrt(ratio) : 0;
                const isZero = v === 0;
                const isRecent = recentQuarter.has(String(cell.period));
                const startsYear = yearBoundaries.has(idx);
                const shareOfRowTotal =
                  Number(acc.total_9l) > 0 ? (v / Number(acc.total_9l)) * 100 : 0;
                return (
                  <Box
                    key={cell.period}
                    sx={{
                      borderLeft: startsYear ? `1.5px solid ${colors.textMuted}55` : 'none',
                      bgcolor: isRecent ? `${colors.chartPrimary}0a` : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip
                      maxWidth={260}
                      title={
                        <Box sx={{ p: 0.25 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.25 }}>
                            {acc.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.5 }}>
                            {formatPeriodLong(String(cell.period), granularity)}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'auto auto',
                              gap: '4px 12px',
                              fontFeatureSettings: '"tnum"',
                              fontSize: 11,
                            }}
                          >
                            <span style={{ opacity: 0.7 }}>Volume</span>
                            <span
                              style={{
                                fontWeight: 700,
                                color: isZero ? colors.textMuted : colors.textPrimary,
                              }}
                            >
                              {isZero
                                ? 'No depletion'
                                : `${fmt6PkNumber(v)} 6-pk · ${num.format(v)} 9L`}
                            </span>
                            {!isZero && (
                              <>
                                <span style={{ opacity: 0.7 }}>% of account total</span>
                                <span style={{ fontWeight: 600 }}>
                                  {shareOfRowTotal.toFixed(1)}%
                                </span>
                                <span style={{ opacity: 0.7 }}>vs intensity max</span>
                                <span style={{ fontWeight: 600 }}>
                                  {Math.round(ratio * 100)}% of p95
                                </span>
                              </>
                            )}
                          </Box>
                          {/* Product breakdown — what they actually
                              ordered that month, sorted by 6-pk desc. */}
                          {!isZero && cell.products && cell.products.length > 0 && (
                            <Box
                              sx={{
                                mt: 0.75,
                                pt: 0.6,
                                borderTop: `1px solid ${colors.borderLight}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: colors.textMuted,
                                  mb: 0.4,
                                }}
                              >
                                Products ordered
                              </Typography>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto auto',
                                  rowGap: '3px',
                                  columnGap: '8px',
                                  fontSize: 11,
                                  fontFeatureSettings: '"tnum"',
                                  alignItems: 'baseline',
                                }}
                              >
                                {cell.products.map((p) => {
                                  const pVol = Number(p.cases_9l);
                                  const sharePct = v > 0 ? (pVol / v) * 100 : 0;
                                  return (
                                    <Box key={p.product_id} sx={{ display: 'contents' }}>
                                      <span
                                        style={{
                                          color: colors.textPrimary,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          fontWeight: 500,
                                        }}
                                        title={p.product_name}
                                      >
                                        {shortSkuName(p.product_name)}
                                      </span>
                                      <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                                        {fmt6PkNumber(pVol)} 6-pk
                                      </span>
                                      <span style={{ color: colors.textMuted, fontWeight: 500 }}>
                                        {sharePct.toFixed(0)}%
                                      </span>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                          {isRecent && (
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: colors.chartPrimary,
                                fontWeight: 700,
                                mt: 0.5,
                                letterSpacing: 0.2,
                                textTransform: 'uppercase',
                              }}
                            >
                              Current quarter
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          height: 22,
                          width: 22,
                          my: 0.5,
                          borderRadius: 0.75,
                          bgcolor: isZero ? '#fff' : `rgba(79, 70, 229, ${intensity.toFixed(3)})`,
                          border: isZero
                            ? `1px solid ${colors.borderLight}`
                            : `1px solid rgba(79, 70, 229, ${Math.min(0.45, intensity + 0.05).toFixed(3)})`,
                          transition: 'transform 0.12s',
                          cursor: 'pointer',
                          '&:hover': { transform: 'scale(1.2)' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                );
              })}

              {/* Cadence pill */}
              <Box
                sx={{
                  pl: 1.5,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  py: 0.5,
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}
              >
                <Tooltip
                  title={
                    <Box sx={{ p: 0.25 }}>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                        {acc.frequency}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.25 }}>
                        Active {acc.months_active} of {months.length} months · longest streak{' '}
                        {acc.derived.longestStreak}.
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.75,
                      bgcolor: `${freqStyle.color}1a`,
                      color: freqStyle.color,
                      fontSize: 10.5,
                      fontWeight: 700,
                      cursor: 'help',
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: freqStyle.color,
                      }}
                    />
                    {acc.months_active}/{months.length}
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------
// Toolbar primitives
// ----------------------------------------------------------------

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

// ----------------------------------------------------------------
// Pill button + group — shared by the data-shape row (Show / Window).
// ----------------------------------------------------------------

interface PillGroupProps {
  label: string;
  children: ReactNode;
}

function PillGroup({ label, children }: PillGroupProps) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
          mr: 0.4,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

interface PillButtonProps {
  active: boolean;
  onClick: () => void;
  tooltip?: string;
  children: ReactNode;
}

function PillButton({ active, onClick, tooltip, children }: PillButtonProps) {
  const button = (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        px: 0.9,
        py: 0.3,
        borderRadius: 0.75,
        fontSize: 10.5,
        fontWeight: active ? 700 : 500,
        color: active ? colors.chartPrimary : colors.textSecondary,
        bgcolor: active ? '#fff' : 'transparent',
        border: `1px solid ${active ? colors.chartPrimary : 'transparent'}`,
        fontFeatureSettings: '"tnum"',
        boxShadow: active ? `0 1px 3px ${colors.chartPrimary}20` : 'none',
        transition: 'all 0.12s',
        '&:hover': {
          borderColor: colors.chartPrimary,
          color: colors.chartPrimary,
        },
      }}
    >
      {children}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
}

function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 1,
        border: `1px solid ${value ? colors.chartPrimary : colors.border}`,
        bgcolor: '#fff',
        minWidth: 220,
        transition: 'border-color 0.12s',
        '&:hover': { borderColor: colors.chartPrimary },
        '&:focus-within': {
          borderColor: colors.chartPrimary,
          boxShadow: `0 0 0 3px ${colors.chartPrimary}1a`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
      <InputBase
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          flex: 1,
          fontSize: 12,
          color: colors.textPrimary,
          '& input::placeholder': { color: colors.textMuted, opacity: 1, fontSize: 11.5 },
        }}
      />
      {value && (
        <Box
          component="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            color: colors.textMuted,
            '&:hover': { bgcolor: colors.surfaceHover, color: colors.textPrimary },
          }}
        >
          <ClearIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Box>
  );
}

interface StateMultiSelectProps {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  open: boolean;
  onToggle: () => void;
}

function StateMultiSelect({
  available,
  selected,
  onChange,
  open,
  onToggle,
}: StateMultiSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  const valueLabel =
    selected.size === 0
      ? 'All states'
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} states`;

  const toggle = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open || selected.size > 0 ? colors.chartPrimary : colors.border}`,
          bgcolor: open || selected.size > 0 ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          State:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {valueLabel}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 280,
            maxWidth: 360,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
            p: 1.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>
              {available.length} states
            </Typography>
            {selected.size > 0 && (
              <Box
                component="button"
                onClick={() => onChange(new Set())}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.chartPrimary,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {available.map((code) => {
              const isSel = selected.has(code);
              return (
                <Box
                  key={code}
                  component="button"
                  onClick={() => toggle(code)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    borderRadius: 0.75,
                    border: `1px solid ${isSel ? colors.chartPrimary : colors.border}`,
                    bgcolor: isSel ? colors.chartPrimary : '#fff',
                    color: isSel ? '#fff' : colors.textPrimary,
                    fontSize: 11.5,
                    fontWeight: isSel ? 700 : 500,
                    fontFeatureSettings: '"tnum"',
                    '&:hover': {
                      borderColor: colors.chartPrimary,
                      bgcolor: isSel ? colors.chartPrimary : `${colors.chartPrimary}10`,
                    },
                  }}
                >
                  {code}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

interface ToolbarMenuProps {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function ToolbarMenu({ label, value, open, onToggle, children }: ToolbarMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open ? colors.chartPrimary : colors.border}`,
          bgcolor: open ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          {label}:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {value}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 240,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
            overflow: 'hidden',
            py: 0.5,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface MenuRowProps {
  selected: boolean;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

function MenuRow({ selected, primary, secondary, onClick }: MenuRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        px: 1.5,
        py: 0.9,
        textAlign: 'left',
        bgcolor: selected ? `${colors.chartPrimary}10` : 'transparent',
        '&:hover': { bgcolor: selected ? `${colors.chartPrimary}18` : colors.surfaceHover },
      }}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: selected ? 700 : 500,
          color: selected ? colors.chartPrimary : colors.textPrimary,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.15 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}
